import { DEFAULT_SCALE } from '@/constants/constants';
import { AppStateAction } from '@/reducers/appReducer';
import { PageShapes } from '@/types/shapes';
import { PdfEditingContext, SubscriptionPlanDto, SubscriptionType, UserDto } from '@tailored-pdf/api';
import React from 'react';
import { FormFields, FormTypes, PageAnnotations, PageImageItems, PageItems, PageTextItems } from '../types';
import { IContext } from '@/types/interfaces';
import { DirtyTextBoxes } from '@/components/PdfEditor/types';

export type AuthState = 'SIGN_IN' | 'SIGN_UP' | 'RESTORE';
export type AuthSource = AuthState | 'AUTO';
export enum UPLOAD_PROGRESS_STATE {
  UPLOAD = 'UPLOAD',
  PROCESS = 'PROCESS',
  PARSE = 'PARSE',
}

export interface AppState {
  authState: { state?: AuthState; force?: boolean };
  authToken?: { token: string; source: AuthSource };
  needPayment: boolean;
  lastPaymentDate?: string;
  preselectedTariff?: SubscriptionType;
  userInfo?: UserDto;
  subscriptionPlans?: SubscriptionPlanDto[];
  pdfLibInstance?: unknown;
  document: File | null;
  documentPassword: string | null;
  savedChanges?: PdfEditingContext;
  pdfObjectsRenderOrder: string[];
  pdfOBjectsZIndexMap: Record<string, number>;
  processedDocument: File | null;
  isDocumentLoaded: boolean;
  isDocumentReady: boolean;
  uploadedFileId: string | null;
  imageToolbarRef?: React.RefObject<HTMLDivElement>;
  textItems: PageTextItems;
  shapes: PageShapes;
  images: PageImageItems;
  annotations: PageAnnotations;
  formFields: FormFields;
  selectedImageId: string | null;
  selectedAnnotationId: string | null;
  selectedFormFieldId: string | null;
  formFieldTypeToInsert: FormTypes | null;
  notification: { type?: 'info' | 'error' | 'success'; message: string; timeoutId?: number };
  pagesMetadata: PageItems;
  deletedPages: number[];
  addedPages: number[];
  scale: number;
  pastStates: AppState[];
  futureStates: AppState[];
  lastActionType: AppStateAction['type'] | null;
  urlsToRevokePerPastState: string[][];
  uploadProgressState: UPLOAD_PROGRESS_STATE | null;
  dirtyTextBoxes: DirtyTextBoxes;
}

export const defaultState: AppState = {
  authState: {},
  needPayment: false,
  document: null,
  documentPassword: null,
  pdfObjectsRenderOrder: [],
  pdfOBjectsZIndexMap: {},
  processedDocument: null,
  isDocumentLoaded: false,
  isDocumentReady: false,
  uploadedFileId: null,
  textItems: {},
  shapes: {},
  images: {},
  annotations: {},
  formFields: {},
  selectedImageId: null,
  selectedAnnotationId: null,
  selectedFormFieldId: null,
  formFieldTypeToInsert: null,
  notification: { type: 'info', message: '' },
  pagesMetadata: {},
  scale: DEFAULT_SCALE,
  addedPages: [],
  deletedPages: [],
  pastStates: [],
  futureStates: [],
  lastActionType: null,
  urlsToRevokePerPastState: [], // TODO: verify if the urls are getting revoked OR else there will be MEMORY LEAK
  uploadProgressState: null,
  dirtyTextBoxes: {},
};

export const Context = React.createContext<IContext<AppState, AppStateAction>>({
  state: defaultState,
  dispatch: () => {},
}); // TODO: Reduce the scope of this context
